import { useEffect, useState } from 'react';
import { contractApi, milestoneApi } from '../services/api';
import type { Contract } from '../types';
import { isMilestoneInDispute } from './personTodos';

/** The stage under objection that an unsigned ÄTA is meant to resolve. */
export interface OngoingObjection {
  milestoneId: string;
  milestoneNumber: number;
}

/**
 * The objection an ÄTA belongs to, when that objection is still open
 * (ClickUp 869fgj7uc). Signing such an ÄTA closes the objection, so the buyer
 * is told before signing; an ordinary ÄTA gets nothing.
 *
 * A separate ÄTA contract links the stage through its conflict scope; an ÄTA
 * made in the guided wizard (a proposed version of the contract) through its
 * settlement. The stage must still be under objection or in conflict.
 */
export async function ongoingObjectionForVariation(contract: Contract): Promise<OngoingObjection | null> {
  if (contract.personSigned) return null;
  let stageId: string | null = null;
  let parentId: string | null = null;
  if (contract.contractType === 'variation') {
    const scope = await contractApi.getConflictVariationScope(contract.id);
    stageId = scope?.milestoneId ?? null;
    parentId = scope?.contractId ?? null;
  } else if (contract.replacesContractId && contract.versionStatus === 'proposed') {
    const settlement = await contractApi.getReplacement(contract.id);
    stageId = settlement?.disputedMilestoneId ?? null;
    parentId = settlement?.originalContractId ?? null;
  }
  if (!stageId || !parentId) return null;
  const stage = (await milestoneApi.getMilestones(parentId)).find(candidate => candidate.id === stageId);
  if (!stage || !(isMilestoneInDispute(stage) || stage.conflictStartedAt)) return null;
  return { milestoneId: stage.id, milestoneNumber: stage.milestoneNumber };
}

/**
 * The open objection for each of these contracts that has one. Contracts are
 * looked up once each; a failed lookup shows no notice.
 */
export function useOngoingObjections(contracts: Contract[]): Record<string, OngoingObjection> {
  const [found, setFound] = useState<Record<string, OngoingObjection>>({});
  // A stable key, so the lookups run when the contracts change, not on every render.
  const key = contracts
    .map(contract => `${contract.id}:${contract.contractType}:${contract.versionStatus ?? ''}:${contract.personSigned ? 1 : 0}`)
    .sort()
    .join('|');
  useEffect(() => {
    let active = true;
    if (!contracts.length) {
      setFound({});
      return () => { active = false; };
    }
    void Promise.all(contracts.map(async contract => {
      try {
        return [contract.id, await ongoingObjectionForVariation(contract)] as const;
      } catch {
        return [contract.id, null] as const;
      }
    })).then(results => {
      if (!active) return;
      const next: Record<string, OngoingObjection> = {};
      results.forEach(([id, objection]) => { if (objection) next[id] = objection; });
      setFound(next);
    });
    return () => { active = false; };
    // `key` stands for `contracts`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return found;
}
